import React, { useState } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import type { Skizze, SkizzenTyp } from '../types';
import { AuswahlFeld, Leerzeile, MarkenFeld, TextFeld } from '../bearbeiten/bausteine';
import { SkizzeSvg } from './SkizzeSvg';
import {
    ZONENPLAN_STANDARD, pruefeZonenplan, zeichneZonenplan,
    type ZonenDurchgang, type ZonenSeite, type ZonenplanParameter,
} from './zonenplan';
import { ABLAUF_STANDARD, zeichneAblauf, type AblaufParameter, type AblaufSchritt } from './ablauf';

/**
 * Die Einstellfelder der beiden neuen Skizzentypen.
 *
 * ═══ WARUM SIE HIER STEHEN UND NICHT IM CONTAINER ═════════════════════════
 * `AsplanModul.tsx` ist der duenne Container des Moduls. Ein Entwurfszustand,
 * der nur bis zum Druck auf „Speichern" lebt, gehoert an die Stelle, an der
 * er bedient wird — sonst waechst der Container um zwei weitere `useState`
 * und einen Ladeeffekt je Skizzentyp.
 *
 * Gezeichnet wird hier NICHT. Diese Datei ruft `zeichneZonenplan` und
 * `zeichneAblauf` auf und reicht das Ergebnis an `SkizzeSvg` weiter; die
 * Aussage der Skizze entsteht in den reinen Funktionen daneben und ist dort
 * ohne DOM geprueft.
 *
 * ═══ WARUM DER ENTWURF ERST AUF KNOPFDRUCK GESPEICHERT WIRD ═══════════════
 * Jeder Schreibvorgang laedt das Buendel neu. Wer bei jedem Zeichen speichert,
 * verliert den Fokus im Feld — im Repo schon einmal passiert. Der Entwurf
 * liegt deshalb lokal, das Blatt zeigt ihn sofort, und gespeichert wird
 * ausdruecklich.
 */

interface Props {
    skizzen: Skizze[];
    gesperrt: boolean;
    onSpeichern: (
        typ: SkizzenTyp, titel: string, parameter: Record<string, unknown>,
    ) => void | Promise<unknown>;
}

const SEITEN: { wert: ZonenSeite; label: string }[] = [
    { wert: 'oben', label: 'oben' },
    { wert: 'unten', label: 'unten' },
    { wert: 'links', label: 'links' },
    { wert: 'rechts', label: 'rechts' },
];

const DURCHGANG_ARTEN: { wert: ZonenDurchgang['art']; label: string }[] = [
    { wert: 'schleuse', label: 'Schwarz-Weiß-Anlage' },
    { wert: 'personen', label: 'Personendurchgang' },
    { wert: 'fahrzeug', label: 'Fahrzeuge (Reifenwaschung)' },
];

const SCHRITT_ARTEN: { wert: AblaufSchritt['art']; label: string }[] = [
    { wert: 'schritt', label: 'Schritt' },
    { wert: 'entscheidung', label: 'Entscheidung' },
];

/**
 * Der gespeicherte Parametersatz eines Skizzentyps, sonst die Vorbelegung.
 *
 * Zusammengefuehrt und nicht ersetzt: ein alter Datensatz, dem ein spaeter
 * hinzugekommenes Feld fehlt, bekommt dafuer die Vorbelegung und nicht
 * `undefined` — sonst faellt die Zeichnung ueber einen fehlenden Schluessel.
 */
const ausGespeichert = <T,>(skizzen: Skizze[], typ: SkizzenTyp, vorbelegung: T): T => {
    const gespeichert = skizzen.find(x => x.typ === typ);
    if (!gespeichert || Object.keys(gespeichert.parameter).length === 0) return vorbelegung;
    return { ...vorbelegung, ...(gespeichert.parameter as unknown as T) };
};

/** Der Knopf, mit dem der Entwurf an den Datensatz geht. */
const SpeicherZeile: React.FC<{
    vorhanden: boolean;
    onSpeichern: () => void;
}> = ({ vorhanden, onSpeichern }) => (
    <div className="flex items-center gap-3 mb-3">
        <button
            type="button"
            onClick={onSpeichern}
            className="px-3 py-1.5 bg-stone-700 text-white rounded-lg text-xs font-bold"
        >
            Einstellung speichern
        </button>
        <span className="text-[11px] text-slate-500">
            {vorhanden
                ? 'Gespeichert: der Plan nutzt diese Einstellung.'
                : 'Noch nicht gespeichert: der Plan zeigt die Vorbelegung.'}
        </span>
    </div>
);

const Fehlerliste: React.FC<{ fehler: string[] }> = ({ fehler }) => (
    fehler.length === 0 ? null : (
        <div className="mb-3 space-y-1">
            {fehler.map((f, i) => (
                <p key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {f}
                </p>
            ))}
        </div>
    )
);

// ═══════════════════════════════════════════════════════════════════════════
// ZONENPLAN
// ═══════════════════════════════════════════════════════════════════════════

export const ZonenplanKarte: React.FC<Props & { bereiche?: string[] }> = ({
    skizzen, gesperrt, onSpeichern, bereiche = [],
}) => {
    // Der gespeicherte Stand gewinnt genau EINMAL, beim ersten Rendern.
    // Danach fuehrt der Entwurf — sonst ueberschriebe das Neuladen nach dem
    // Speichern die laufende Eingabe. Bewusst als Anfangswert und nicht als
    // Effekt: ein Effekt, der beim ersten Lauf setState ruft, erzeugt eine
    // zweite Renderrunde und ist genau das, wovor die React-Regel warnt.
    const [p, setP] = useState<ZonenplanParameter>(
        () => ausGespeichert(skizzen, 'zonenplan', ZONENPLAN_STANDARD));

    const bild = zeichneZonenplan(p);
    const fehler = pruefeZonenplan(p);
    const durchgangAendern = (i: number, u: Partial<ZonenDurchgang>) =>
        setP(s => ({ ...s, durchgaenge: s.durchgaenge.map((d, j) => (j === i ? { ...d, ...u } : d)) }));

    return (
        <>
            <div className="grid sm:grid-cols-3 gap-2 mb-3">
                <TextFeld
                    label="Schwarzbereich"
                    wert={p.schwarz_name}
                    gesperrt={gesperrt}
                    onSpeichern={v => setP(s => ({ ...s, schwarz_name: v }))}
                />
                <TextFeld
                    label="Übergangsbereich (Grau)"
                    wert={p.grau_name}
                    gesperrt={gesperrt}
                    onSpeichern={v => setP(s => ({ ...s, grau_name: v }))}
                />
                <TextFeld
                    label="Weißbereich"
                    wert={p.weiss_name}
                    gesperrt={gesperrt}
                    onSpeichern={v => setP(s => ({ ...s, weiss_name: v }))}
                />
            </div>

            <div className="mb-3">
                <MarkenFeld
                    label="Arbeitsbereiche im Schwarzbereich"
                    werte={p.schwarz_bereiche}
                    vorschlaege={bereiche}
                    gesperrt={gesperrt}
                    platzhalter="Kennung"
                    onSpeichern={v => setP(s => ({ ...s, schwarz_bereiche: v }))}
                />
            </div>

            <div className="grid sm:grid-cols-2 gap-2 mb-3">
                <TextFeld
                    label="Absperrung"
                    wert={p.absperrung}
                    gesperrt={gesperrt}
                    hinweis="Die Absperrung IST die Bereichsgrenze; sie wird örtlich abgesteckt."
                    onSpeichern={v => setP(s => ({ ...s, absperrung: v }))}
                />
                <TextFeld
                    label="Zwischenlager (im Schwarzbereich)"
                    wert={p.zwischenlager}
                    gesperrt={gesperrt}
                    hinweis="Leer lassen, wenn keines vorgesehen ist."
                    onSpeichern={v => setP(s => ({ ...s, zwischenlager: v }))}
                />
                <TextFeld
                    label="BE-Fläche (Weißbereich)"
                    wert={p.be_flaeche}
                    gesperrt={gesperrt}
                    onSpeichern={v => setP(s => ({ ...s, be_flaeche: v }))}
                />
                <TextFeld
                    label="Sammelplatz (Weißbereich)"
                    wert={p.sammelplatz}
                    gesperrt={gesperrt}
                    hinweis="Ohne Sammelplatz endet der Rettungsweg nirgends."
                    onSpeichern={v => setP(s => ({ ...s, sammelplatz: v }))}
                />
                <TextFeld
                    label="Rettungsweg"
                    wert={p.rettungsweg}
                    gesperrt={gesperrt}
                    onSpeichern={v => setP(s => ({ ...s, rettungsweg: v }))}
                />
            </div>

            <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1">
                Durchgänge durch die Absperrung
            </p>
            {p.durchgaenge.length === 0 && <Leerzeile text="Kein Durchgang gekennzeichnet." />}
            <ul className="space-y-2 mb-3">
                {p.durchgaenge.map((d, i) => (
                    <li key={i} className="grid sm:grid-cols-[1fr_12rem_8rem_2rem] gap-2 items-end">
                        <TextFeld
                            label="Bezeichnung"
                            wert={d.name}
                            gesperrt={gesperrt}
                            onSpeichern={v => durchgangAendern(i, { name: v })}
                        />
                        <AuswahlFeld
                            label="Art"
                            wert={d.art}
                            optionen={DURCHGANG_ARTEN}
                            gesperrt={gesperrt}
                            onSpeichern={v => durchgangAendern(i, { art: v })}
                        />
                        <AuswahlFeld
                            label="Seite"
                            wert={d.seite}
                            optionen={SEITEN}
                            gesperrt={gesperrt}
                            onSpeichern={v => durchgangAendern(i, { seite: v })}
                        />
                        {!gesperrt && (
                            <button
                                type="button"
                                onClick={() => setP(s => ({
                                    ...s, durchgaenge: s.durchgaenge.filter((_, j) => j !== i),
                                }))}
                                title="Durchgang entfernen"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {!gesperrt && (
                <button
                    type="button"
                    onClick={() => setP(s => ({
                        ...s,
                        durchgaenge: [...s.durchgaenge, { name: '', art: 'personen', seite: 'oben' }],
                    }))}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 border border-slate-300
                               rounded-lg text-xs font-bold mb-3"
                >
                    <Plus className="w-3.5 h-3.5" /> Durchgang
                </button>
            )}

            {!gesperrt && (
                <SpeicherZeile
                    vorhanden={skizzen.some(x => x.typ === 'zonenplan')}
                    onSpeichern={() => void onSpeichern(
                        'zonenplan', bild.titel, p as unknown as Record<string, unknown>)}
                />
            )}

            <Fehlerliste fehler={fehler} />
            <SkizzeSvg zeichnung={bild} />
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// ABLAUFDIAGRAMM
// ═══════════════════════════════════════════════════════════════════════════

export const AblaufKarte: React.FC<Props> = ({ skizzen, gesperrt, onSpeichern }) => {
    const [p, setP] = useState<AblaufParameter>(
        () => ausGespeichert(skizzen, 'ablauf', ABLAUF_STANDARD));

    const bild = zeichneAblauf(p);
    const schrittAendern = (i: number, u: Partial<AblaufSchritt>) =>
        setP(s => ({ ...s, schritte: s.schritte.map((x, j) => (j === i ? { ...x, ...u } : x)) }));

    return (
        <>
            <ul className="space-y-2 mb-3">
                {p.schritte.map((s, i) => (
                    <li key={i} className="border border-slate-200 rounded-xl p-2.5">
                        <div className="grid sm:grid-cols-[4rem_1fr_9rem_2rem] gap-2 items-end">
                            <TextFeld
                                label="Nr."
                                wert={s.nr}
                                gesperrt={gesperrt}
                                onSpeichern={v => schrittAendern(i, { nr: v })}
                            />
                            <TextFeld
                                label={s.art === 'entscheidung' ? 'Frage' : 'Schritt'}
                                wert={s.text}
                                gesperrt={gesperrt}
                                onSpeichern={v => schrittAendern(i, { text: v })}
                            />
                            <AuswahlFeld
                                label="Art"
                                wert={s.art}
                                optionen={SCHRITT_ARTEN}
                                gesperrt={gesperrt}
                                onSpeichern={v => schrittAendern(i, { art: v })}
                            />
                            {!gesperrt && (
                                <button
                                    type="button"
                                    onClick={() => setP(x => ({
                                        ...x, schritte: x.schritte.filter((_, j) => j !== i),
                                    }))}
                                    title="Schritt entfernen"
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {s.art === 'entscheidung' && (
                            <div className="grid sm:grid-cols-2 gap-2 mt-2">
                                <TextFeld
                                    label="Ausgang „ja“"
                                    wert={s.ja ?? ''}
                                    gesperrt={gesperrt}
                                    onSpeichern={v => schrittAendern(i, { ja: v })}
                                />
                                <TextFeld
                                    label="Ausgang „nein“"
                                    wert={s.nein ?? ''}
                                    gesperrt={gesperrt}
                                    onSpeichern={v => schrittAendern(i, { nein: v })}
                                />
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {!gesperrt && (
                <div className="flex flex-wrap gap-2 mb-3">
                    <button
                        type="button"
                        onClick={() => setP(s => ({
                            ...s,
                            schritte: [...s.schritte, {
                                nr: `(${s.schritte.length + 1})`, text: '', art: 'schritt',
                            }],
                        }))}
                        className="inline-flex items-center gap-1.5 px-2 py-1.5 border border-slate-300
                                   rounded-lg text-xs font-bold"
                    >
                        <Plus className="w-3.5 h-3.5" /> Schritt
                    </button>
                    <button
                        type="button"
                        onClick={() => setP(ABLAUF_STANDARD)}
                        className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs font-bold"
                    >
                        Regelablauf laden
                    </button>
                </div>
            )}

            {!gesperrt && (
                <SpeicherZeile
                    vorhanden={skizzen.some(x => x.typ === 'ablauf')}
                    onSpeichern={() => void onSpeichern(
                        'ablauf', bild.titel, p as unknown as Record<string, unknown>)}
                />
            )}

            <SkizzeSvg zeichnung={bild} />
        </>
    );
};
